import React, { useState, useEffect } from 'react';
import { useUserTimezone } from '../../hooks/useUserTimezone';
import { useUnitPreferences, convertWater } from '../../utils/unitConversions';
import { getScoreColor } from '../../../config/scoring';

interface Goal {
  goal_id: string;
  goal_name: string;
  goal_value: number;
  goal_type: 'over' | 'under' | 'target' | 'range' | 'boolean';
  variance_percent?: number;
  unit: string;
  health_data_field: string;
  category: string;
  status: 'enabled' | 'disabled';
}

interface MetricItem {
  label: string;
  target: number;
  actual?: number;
  unit: string;
  icon: string;
  healthDataField: string;
  goalType: 'over' | 'under' | 'target' | 'boolean';
}

interface DailyMetricsTileProps {
  onExpand?: () => void;
}

// Core metrics mapping (4 metrics in fixed order)
const CORE_METRICS_MAP: Record<string, { icon: string; label: string }> = {
  'steps': { icon: '👟', label: 'Steps' },
  'water_intake': { icon: '💧', label: 'Water' },
  'sleep_hours': { icon: '😴', label: 'Sleep' },
  'calories': { icon: '🔥', label: 'Calories' }
};

const CORE_METRICS_ORDER = ['steps', 'water_intake', 'sleep_hours', 'calories'];

export const DailyMetricsTile: React.FC<DailyMetricsTileProps> = ({ onExpand }) => {
  const { getToday } = useUserTimezone();
  const prefs = useUnitPreferences();
  const [_goals, setGoals] = useState<Goal[]>([]);
  const [metrics, setMetrics] = useState<MetricItem[]>([]);
  const [dailyScore, setDailyScore] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Helper function to calculate progress percentage
  const getProgressPercentage = (actual: number | undefined, target: number): number => {
    if (!actual || actual === 0) return 0;
    return Math.min(100, (actual / target) * 100);
  };

  // Helper function to get status color based on goal type and progress
  const getStatusColor = (actual: number | undefined, target: number, goalType: string): string => {
    if (!actual || actual === 0) return '#6b7280'; // gray for no data

    const percentage = (actual / target) * 100;

    switch (goalType) {
      case 'over':
        if (percentage >= 100) return '#10b981'; // green - excellent
        if (percentage >= 80) return '#84cc16'; // lime - good
        if (percentage >= 60) return '#f59e0b'; // amber - warning
        return '#ef4444'; // red - poor

      case 'under':
        if (percentage <= 100) return '#10b981'; // green - excellent
        if (percentage <= 120) return '#84cc16'; // lime - good
        if (percentage <= 140) return '#f59e0b'; // amber - warning
        return '#ef4444'; // red - poor

      case 'target':
      case 'range':
        if (percentage >= 95 && percentage <= 105) return '#10b981'; // green - excellent
        if (percentage >= 90 && percentage <= 110) return '#84cc16'; // lime - good
        if (percentage >= 80 && percentage <= 120) return '#f59e0b'; // amber - warning
        return '#ef4444'; // red - poor

      default:
        return '#6b7280';
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('auth_token');

        // Fetch goals, today's metrics, and today's daily record in parallel
        // Use timezone-aware "today" calculation
        const today = getToday();
        const [goalsResponse, metricsResponse, dailyRecordResponse] = await Promise.all([
          fetch('/api/goals', {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          }),
          fetch('/api/health/metrics/latest', {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          }),
          fetch(`/api/health/daily-record/${today}`, {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          })
        ]);

        const goalsData = await goalsResponse.json();
        await metricsResponse.json();
        const dailyRecordData = await dailyRecordResponse.json();

        if (goalsData.success && goalsData.goals) {
          // Convert goals object to array and filter enabled goals
          const goalsArray = Object.values(goalsData.goals) as Goal[];
          const enabledGoals = goalsArray.filter(g => g.status === 'enabled');
          setGoals(enabledGoals);

          // Build metrics from daily record's targets_at_time (point-in-time snapshot)
          const metricsArray: MetricItem[] = [];
          const dailyRecord = dailyRecordData.success && dailyRecordData.record ? dailyRecordData.record : null;

          // Get today's actual values from daily record (NOT from metrics/latest)
          const todayActualValues = dailyRecord?.actual_values || {};

          if (dailyRecord && dailyRecord.targets_at_time) {
            // Use targets from daily record (locked at start of day)
            CORE_METRICS_ORDER.forEach(fieldName => {
              const targetInfo = dailyRecord.targets_at_time[fieldName];
              const metricConfig = CORE_METRICS_MAP[fieldName];

              if (targetInfo && metricConfig) {
                metricsArray.push({
                  label: metricConfig.label,
                  target: (targetInfo as any).goal_value || (targetInfo as any).value,
                  unit: fieldName === 'water_intake' ? 'oz' : fieldName === 'sleep_hours' ? 'hours' : fieldName === 'steps' ? 'steps' : 'calories',
                  icon: metricConfig.icon,
                  healthDataField: fieldName,
                  goalType: (targetInfo as any).goal_type || (targetInfo as any).type,
                  actual: todayActualValues[fieldName] || 0
                });
              }
            });
          } else {
            // Fallback to current goals.json if no daily record exists yet (first data entry of the day)
            CORE_METRICS_ORDER.forEach(fieldName => {
              const goal = enabledGoals.find(g => g.health_data_field === fieldName);
              const metricConfig = CORE_METRICS_MAP[fieldName];

              if (goal && metricConfig) {
                metricsArray.push({
                  label: metricConfig.label,
                  target: goal.goal_value,
                  unit: goal.unit,
                  icon: metricConfig.icon,
                  healthDataField: fieldName,
                  goalType: goal.goal_type,
                  actual: todayActualValues[fieldName] || 0
                });
              }
            });
          }

          setMetrics(metricsArray);

          // Extract daily score from daily record
          console.log('Daily record response:', dailyRecordData);
          console.log('Calculated score:', dailyRecordData.record?.calculated_score);

          if (dailyRecordData.success && dailyRecordData.record?.calculated_score !== undefined) {
            console.log('Setting daily score to:', dailyRecordData.record.calculated_score);
            setDailyScore(dailyRecordData.record.calculated_score);
          } else {
            console.log('Daily score not set - condition failed');
          }
        } else {
          setError('Failed to load goals');
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load metrics');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div
        className="daily-metrics-tile"
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          padding: '12px',
          backgroundColor: 'var(--bg-primary)',
          border: '1px solid var(--border-color)',
          borderRadius: 'var(--radius-medium)',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
          Loading metrics...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="daily-metrics-tile"
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          padding: '12px',
          backgroundColor: 'var(--bg-primary)',
          border: '1px solid var(--border-color)',
          borderRadius: 'var(--radius-medium)',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <div style={{ color: 'var(--text-danger)', fontSize: '14px' }}>
          {error}
        </div>
      </div>
    );
  }

  if (metrics.length === 0) {
    return (
      <div
        className="daily-metrics-tile"
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          padding: '12px',
          backgroundColor: 'var(--bg-primary)',
          border: '1px solid var(--border-color)',
          borderRadius: 'var(--radius-medium)',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <div style={{
          color: 'var(--text-secondary)',
          fontSize: '14px',
          textAlign: 'center',
          padding: '20px'
        }}>
          <div style={{ marginBottom: '8px' }}>📊</div>
          <div>No active goals set</div>
          <div style={{ fontSize: '12px', marginTop: '4px' }}>
            Visit Goals & Progress to create health goals
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="daily-metrics-tile"
      onClick={onExpand}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: '12px',
        cursor: onExpand ? 'pointer' : 'default',
        backgroundColor: 'var(--bg-primary)',
        border: '1px solid var(--border-color)',
        borderRadius: 'var(--radius-medium)',
        position: 'relative'
      }}
    >
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px'
      }}>
        <div>
          <h3 style={{
            fontSize: '16px',
            fontWeight: '600',
            margin: '0',
            color: 'var(--text-primary)'
          }}>
            Today's Metrics
          </h3>
          <div style={{
            fontSize: '13px',
            color: 'var(--text-secondary)',
            marginTop: '2px'
          }}>
            {new Date().toLocaleDateString('en-US', { 
              month: 'short', 
              day: 'numeric' 
            })}
          </div>
        </div>
        {onExpand && (
          <div style={{
            color: 'var(--text-secondary)',
            fontSize: '12px'
          }}>
            <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            </svg>
          </div>
        )}
      </div>

      {/* Metrics Grid */}
      <div style={{
        flex: 1,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gridTemplateRows: '1fr 1fr',
        gap: '8px'
      }}>
        {metrics.map((metric, index) => {
          const progress = getProgressPercentage(metric.actual, metric.target);
          const statusColor = getStatusColor(metric.actual, metric.target, metric.goalType);

          return (
            <div
              key={index}
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                backgroundColor: 'var(--bg-secondary)',
                borderRadius: 'var(--radius-small)',
                padding: '10px',
                border: `1px solid var(--border-color)`,
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              {/* Progress Background */}
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  height: '100%',
                  width: `${progress}%`,
                  backgroundColor: statusColor,
                  opacity: 0.1,
                  transition: 'width 0.3s ease'
                }}
              />

              {/* Content */}
              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  marginBottom: '6px'
                }}>
                  <span style={{ fontSize: '14px' }}>{metric.icon}</span>
                  <span style={{
                    fontSize: '11px',
                    fontWeight: '500',
                    color: 'var(--text-secondary)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    {metric.label}
                  </span>
                </div>

                {/* Actual value */}
                {metric.actual !== undefined && metric.actual > 0 && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: '3px',
                    marginBottom: '4px'
                  }}>
                    <span style={{
                      fontSize: '20px',
                      fontWeight: '700',
                      color: metric.actual >= metric.target ? '#10b981' : '#f59e0b'
                    }}>
                      {metric.healthDataField === 'water_intake'
                        ? convertWater(metric.actual, prefs.units.water).toLocaleString()
                        : metric.actual.toLocaleString()
                      }
                    </span>
                    {metric.unit && (
                      <span style={{
                        fontSize: '11px',
                        color: 'var(--text-secondary)'
                      }}>
                        {metric.healthDataField === 'water_intake' ? prefs.units.water : metric.unit}
                      </span>
                    )}
                  </div>
                )}

                {/* Goal value */}
                <div style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: '3px'
                }}>
                  <span style={{
                    fontSize: '10px',
                    color: 'var(--text-secondary)',
                    fontWeight: '500',
                    textTransform: 'uppercase'
                  }}>
                    Goal:
                  </span>
                  <span style={{
                    fontSize: '12px',
                    fontWeight: '500',
                    color: 'var(--text-secondary)'
                  }}>
                    {metric.healthDataField === 'water_intake'
                      ? convertWater(metric.target, prefs.units.water).toLocaleString()
                      : metric.target.toLocaleString()
                    }
                  </span>
                  {metric.unit && (
                    <span style={{
                      fontSize: '10px',
                      color: 'var(--text-secondary)'
                    }}>
                      {metric.healthDataField === 'water_intake' ? prefs.units.water : metric.unit}
                    </span>
                  )}
                </div>
              </div>

              {/* Status Indicator */}
              <div
                style={{
                  position: 'absolute',
                  top: '4px',
                  right: '4px',
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: statusColor
                }}
              />
            </div>
          );
        })}
      </div>

      {/* Overall Score */}
      {dailyScore !== null && (() => {
        console.log('RENDERING DAILY SCORE SECTION - dailyScore value:', dailyScore);
        console.log('RENDERING DAILY SCORE SECTION - typeof dailyScore:', typeof dailyScore);
        console.log('RENDERING DAILY SCORE SECTION - dailyScore === null?', dailyScore === null);
        console.log('RENDERING DAILY SCORE SECTION - dailyScore === undefined?', dailyScore === undefined);
        return (
          <div style={{
            marginTop: '8px',
            padding: '8px',
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: 'var(--radius-small)',
            border: '1px solid var(--border-color)',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Progress Background */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                height: '100%',
                width: `${dailyScore}%`,
                backgroundColor: getScoreColor(dailyScore),
                opacity: 0.1,
                transition: 'width 0.3s ease'
              }}
            />

            {/* Content */}
            <div style={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'center',
                gap: '6px'
              }}>
                <span style={{
                  fontSize: '10px',
                  textTransform: 'uppercase',
                  color: 'var(--text-secondary)',
                  fontWeight: '500',
                  letterSpacing: '0.5px'
                }}>
                  DAILY SCORE:
                </span>
                <span style={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: getScoreColor(dailyScore)
                }}>
                  {Math.round(dailyScore)}/100
                </span>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};